const ACCESS_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";

let cachedAccessToken: string | null = null;
let cachedRefreshToken: string | null = null;

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readSessionValue = (key: string): string | null => {
  try {
    return getSessionStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeSessionValue = (key: string, value: string): void => {
  try {
    getSessionStorage()?.setItem(key, value);
  } catch {
    return;
  }
};

const removeSessionValue = (key: string): void => {
  try {
    getSessionStorage()?.removeItem(key);
  } catch {
    return;
  }
};

/** One-time migration: drop legacy persistent localStorage tokens. */
const migrateLegacyLocalStorageTokens = (): void => {
  if (typeof window === "undefined") return;

  try {
    const legacyAccess = window.localStorage.getItem(ACCESS_TOKEN_KEY);
    const legacyRefresh = window.localStorage.getItem(REFRESH_TOKEN_KEY);

    if (legacyAccess && !readSessionValue(ACCESS_TOKEN_KEY)) {
      writeSessionValue(ACCESS_TOKEN_KEY, legacyAccess);
    }
    if (legacyRefresh && !readSessionValue(REFRESH_TOKEN_KEY)) {
      writeSessionValue(REFRESH_TOKEN_KEY, legacyRefresh);
    }

    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    return;
  }
};

migrateLegacyLocalStorageTokens();

const loadAccessToken = (): string | null => {
  if (cachedAccessToken !== null) return cachedAccessToken || null;
  cachedAccessToken = readSessionValue(ACCESS_TOKEN_KEY) ?? "";
  return cachedAccessToken || null;
};

const loadRefreshToken = (): string | null => {
  if (cachedRefreshToken !== null) return cachedRefreshToken || null;
  cachedRefreshToken = readSessionValue(REFRESH_TOKEN_KEY) ?? "";
  return cachedRefreshToken || null;
};

export const getHasJWT = (): boolean => !!loadAccessToken();
export const getJWT = (): string | null => loadAccessToken();
export const setJWT = (authToken: string): void => {
  cachedAccessToken = authToken;
  if (authToken) {
    writeSessionValue(ACCESS_TOKEN_KEY, authToken);
  } else {
    removeSessionValue(ACCESS_TOKEN_KEY);
  }
};

export const getHasRefreshToken = (): boolean => !!loadRefreshToken();
export const getRefreshToken = (): string | null => loadRefreshToken();
export const setRefreshToken = (refreshToken: string): void => {
  cachedRefreshToken = refreshToken;
  if (refreshToken) {
    writeSessionValue(REFRESH_TOKEN_KEY, refreshToken);
  } else {
    removeSessionValue(REFRESH_TOKEN_KEY);
  }
};

// Treat a token as expired slightly before it actually is, so a request that
// leaves here valid does not arrive expired after network latency.
const EXPIRY_SKEW_SECONDS = 30;

const decodeJWTPayload = (token: string): { exp?: number } | null => {
  const segments = token.split(".");
  if (segments.length !== 3) return null;

  try {
    const base64 = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload === "object" && payload !== null ? payload : null;
  } catch {
    return null;
  }
};

/**
 * Whether the access token is past (or within EXPIRY_SKEW_SECONDS of) its exp.
 *
 * Reading `exp` client-side is only ever used to decide whether to refresh
 * early — the server remains the authority on whether a token is accepted, so
 * a token we cannot parse is reported as *not* expired and simply sent as-is.
 * A missing token, by contrast, is expired: there is nothing to send.
 */
export const isTokenExpired = (
  token: string | null,
  skewSeconds: number = EXPIRY_SKEW_SECONDS
): boolean => {
  if (!token) return true;

  const payload = decodeJWTPayload(token);
  if (typeof payload?.exp !== "number") return false;

  return payload.exp * 1000 <= Date.now() + skewSeconds * 1000;
};
